import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DecimalPipe } from '@angular/common';
import {
  faBackwardStep,
  faCaretLeft,
  faCaretRight,
  faForwardStep,
} from '@fortawesome/free-solid-svg-icons';
import { SuiButtonComponent } from '@sensika/sui/button';

/**
 * A type-a-page pager: first/prev/next/last ghost icon buttons + a page `<input>` and `/ pageCount`.
 * Built from sui primitives (ghost `sui-button`s) so it matches the toolbar's fidelity; the nav math
 * is trivial enough (`goTo` + boundary checks) that it's computed inline rather than on a headless engine.
 *
 * Public API mirrors the legacy `app-pagination` 1:1 so consumers swap with no binding changes:
 * inputs `collectionSize`/`currentPage`/`pageSize`; outputs `pageChanged(number)` (nav clicks) and
 * `doPageChange(string)` (input Enter). The input only emits its raw value — the parent processes it
 * and round-trips the new page back through `[currentPage]` (matches the legacy controlled behavior).
 */
@Component({
  selector: 'sui-pagination',
  standalone: true,
  imports: [DecimalPipe, SuiButtonComponent],
  templateUrl: './sui-pagination.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sui-pagination.component.scss',
})
export class SuiPaginationComponent {
  @ViewChild('pageInput') pageInput: ElementRef<HTMLInputElement>;

  @Input() collectionSize = 0;
  @Input() currentPage = 1;
  @Input() pageSize = 0;

  @Output() pageChanged = new EventEmitter<number>();
  @Output() doPageChange = new EventEmitter<string>();

  protected readonly faFirst = faBackwardStep;
  protected readonly faPrev = faCaretLeft;
  protected readonly faNext = faCaretRight;
  protected readonly faLast = faForwardStep;

  /** Total pages — computed from the collection, same as the legacy `maxPage`. */
  get pageCount(): number {
    return Math.ceil(this.collectionSize / this.pageSize);
  }

  get isFirst(): boolean {
    return this.currentPage <= 1;
  }

  get isLast(): boolean {
    return this.currentPage >= this.pageCount;
  }

  /** Nav buttons emit a clamped page number (buttons are `[disabled]` at the boundaries anyway). */
  goTo(page: number): void {
    const clamped = Math.min(Math.max(page, 1), this.pageCount);
    if (clamped !== this.currentPage) {
      this.pageChanged.emit(clamped);
    }
  }

  /** Enter in the page input: emit the raw string; the parent round-trips it via `currentPage`. */
  onInputEnter(value: string): void {
    this.doPageChange.emit(value);
  }

  /** Clamp the typed value to the last page while typing, mirroring the legacy `handleMaxInput`. */
  clampInput(): void {
    const el = this.pageInput.nativeElement;
    if (+el.value > this.pageCount) {
      el.value = String(this.pageCount);
    }
  }
}
